import {
  convertSnakeCaseToReadableString,
  getUniqueId,
  type TFunction,
} from "@jugl-web/utils";
import {
  InstagramAccountModel,
  TwilioAccountModel,
  WebChatBotAccountModel,
  WhatsappAgentModel,
  CRMChannel,
} from "@jugl-web/rest-api";
import { ReactComponent as AdminIcon } from "./assets/admin.svg";
import { ReactComponent as CompanyIcon } from "./assets/company.svg";
import { ReactComponent as CustomIcon } from "./assets/custom.svg";
import { ReactComponent as EmailIcon } from "./assets/email.svg";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import { ReactComponent as LinkedinIcon } from "./assets/linkedin.svg";
import { ReactComponent as MoneyIcon } from "./assets/money.svg";
import { ReactComponent as PhoneIcon } from "./assets/phone.svg";
import { ReactComponent as WorldIcon } from "./assets/world.svg";
import { ReactComponent as ChatBotIcon } from "./assets/chat-bot.svg";
import { ReactComponent as InstagramIcon } from "./assets/instagram.svg";
import { ReactComponent as TwilioIcon } from "./assets/twilio.svg";
import { ReactComponent as WhatsappIcon } from "./assets/whatsapp.svg";
import type {
  ContactInfoChannelsValues,
  ContactInfoVisitorIntelligenceValues,
  ContactInfoProfileValues,
  ContactIntelligenceRowData,
} from "./types";

export const getProfileSectionRows = (
  profileValues: ContactInfoProfileValues,
  t: TFunction
): ContactIntelligenceRowData[] => [
  {
    id: "phone",
    Icon: PhoneIcon,
    label: t({
      id: "common.phone-number",
      defaultMessage: "Phone Number",
    }),
    value: profileValues.phoneNumber ?? null,
  },
  {
    id: "email",
    Icon: EmailIcon,
    label: t({
      id: "common.email",
      defaultMessage: "Email",
    }),
    value: profileValues.email ?? null,
  },
  ...Object.entries(profileValues.info ?? {}).map(([fieldKey, value]) => ({
    id: fieldKey,
    Icon: CustomIcon,
    label: convertSnakeCaseToReadableString(fieldKey),
    value: value ?? null,
  })),
];

export const getVistorIntelligenceSectionRows = (
  visitorIntelligence: ContactInfoVisitorIntelligenceValues,
  t: TFunction
): ContactIntelligenceRowData[] => [
  {
    id: "phone",
    Icon: PhoneIcon,
    label: t({
      id: "common.phone-number",
      defaultMessage: "Phone Number",
    }),
    value: visitorIntelligence.phoneNumber ?? null,
  },
  {
    id: "personal-email",
    Icon: EmailIcon,
    label: t({
      id: "contacts-page.personal-email",
      defaultMessage: "Personal Email",
    }),
    value: visitorIntelligence.personalEmail ?? null,
  },
  {
    id: "business-email",
    Icon: EmailIcon,
    label: t({
      id: "contacts-page.business-email",
      defaultMessage: "Business Email",
    }),
    value: visitorIntelligence.businessEmail ?? null,
  },
  {
    id: "location",
    Icon: WorldIcon,
    label: t({
      id: "contacts-page.location",
      defaultMessage: "Location",
    }),
    value: visitorIntelligence.location ?? null,
  },
  {
    id: "personal-linkedIn",
    Icon: LinkedinIcon,
    label: t({
      id: "contacts-page.personal-linkedin",
      defaultMessage: "Personal LinkedIn",
    }),
    value: visitorIntelligence.linkedin ?? null,
  },
  {
    id: "company",
    Icon: CompanyIcon,
    label: t({
      id: "contacts-page.company",
      defaultMessage: "Company",
    }),
    value: visitorIntelligence.company ?? null,
  },
  {
    id: "revenue",
    Icon: MoneyIcon,
    label: t({
      id: "contacts-page.revenue",
      defaultMessage: "Revenue",
    }),
    value: visitorIntelligence.revenue ?? null,
  },
  {
    id: "industry",
    Icon: InfoIcon,
    label: t({
      id: "contacts-page.industry",
      defaultMessage: "Industry",
    }),
    value: visitorIntelligence.industry ?? null,
  },
  {
    id: "title",
    Icon: AdminIcon,
    label: t({
      id: "contacts-page.title",
      defaultMessage: "Title",
    }),
    value: visitorIntelligence.title ?? null,
  },
  {
    id: "website",
    Icon: WorldIcon,
    label: t({
      id: "contacts-page.website",
      defaultMessage: "Website",
    }),
    value: visitorIntelligence.website ?? null,
  },
  {
    id: "business-linkedIn",
    Icon: LinkedinIcon,
    label: t({
      id: "contacts-page.business-linkedin",
      defaultMessage: "Business LinkedIn",
    }),
    value: visitorIntelligence.companyLinkedin ?? null,
  },
];

const mapContactSourceToCrmChannel = (src: string): CRMChannel | null => {
  switch (src) {
    case CRMChannel.whatsapp:
      return CRMChannel.whatsapp;
    case CRMChannel.instagram:
      return CRMChannel.instagram;
    case CRMChannel.chatbot:
      return CRMChannel.chatbot;
    case CRMChannel.twilio:
      return CRMChannel.twilio;
    default:
      return null;
  }
};

const getChannelLabel = (
  channel: CRMChannel,
  occurrence: number,
  t: TFunction
): string => {
  const labels: Record<CRMChannel, string> = {
    [CRMChannel.whatsapp]: t({
      id: "contacts-page.whatsapp",
      defaultMessage: "WhatsApp",
    }),
    [CRMChannel.instagram]: t({
      id: "contacts-page.instagram",
      defaultMessage: "Instagram",
    }),
    [CRMChannel.chatbot]: t({
      id: "contacts-page.web-chat",
      defaultMessage: "Web chat",
    }),
    [CRMChannel.twilio]: t({
      id: "contacts-page.text-sms",
      defaultMessage: "Text (SMS)",
    }),
  };

  const baseLabel = labels[channel];

  return occurrence > 1 ? `${baseLabel} #${occurrence}` : baseLabel;
};

const channelIcons: Record<CRMChannel, ContactIntelligenceRowData["Icon"]> = {
  [CRMChannel.whatsapp]: WhatsappIcon,
  [CRMChannel.instagram]: InstagramIcon,
  [CRMChannel.chatbot]: ChatBotIcon,
  [CRMChannel.twilio]: TwilioIcon,
};

const getChannelIcon = (
  channel: CRMChannel
): ContactIntelligenceRowData["Icon"] => channelIcons[channel];

const isNonEmptyAccount = <T extends object | null | undefined>(
  account: T
): account is Exclude<T, null | undefined> =>
  !!account && Object.keys(account).length > 0;

export const getContactChannelConnectionStatus = (crmConfig?: {
  chatbot: WebChatBotAccountModel | null;
  instagram: InstagramAccountModel | null;
  twilio: TwilioAccountModel | null;
  whatsapp: WhatsappAgentModel | null;
}): Record<CRMChannel, boolean> => {
  const whatsappAgent = isNonEmptyAccount(crmConfig?.whatsapp)
    ? crmConfig.whatsapp
    : undefined;
  const instagramAccount = isNonEmptyAccount(crmConfig?.instagram)
    ? crmConfig.instagram
    : undefined;
  const webChatBotAccount = isNonEmptyAccount(crmConfig?.chatbot)
    ? crmConfig.chatbot
    : undefined;

  return {
    [CRMChannel.whatsapp]: whatsappAgent?.phone_number.status === "CONNECTED",
    [CRMChannel.instagram]: instagramAccount?.status === "connected",
    [CRMChannel.chatbot]: !!webChatBotAccount?.enabled,
    [CRMChannel.twilio]: !!crmConfig?.twilio?.id,
  };
};

export const getChannelsSectionRows = (
  channelValues: ContactInfoChannelsValues,
  t: TFunction,
  channelConnectionStatus?: Record<CRMChannel, boolean>
): ContactIntelligenceRowData[] => {
  const occurrencesByChannel = new Map<CRMChannel, number>();

  return channelValues.conversations.flatMap(({ conv_id, src }) => {
    const normalizedSource = src.trim().toLowerCase();
    const channel = mapContactSourceToCrmChannel(normalizedSource);
    if (!channel) {
      return [];
    }

    const Icon = getChannelIcon(channel);
    const occurrence = (occurrencesByChannel.get(channel) ?? 0) + 1;

    occurrencesByChannel.set(channel, occurrence);

    const label = getChannelLabel(channel, occurrence, t);

    return [
      {
        id: getUniqueId(),
        Icon,
        label,
        value: {
          type: "conversation",
          value: t({
            id: "contacts-page.conversation",
            defaultMessage: "Conversation",
          }),
          channel,
          convId: conv_id,
          isChannelAccountConnected: !!channelConnectionStatus?.[channel],
        },
      },
    ];
  });
};
